import { AxiosInstance } from "axios";
import ApiClient from "./ApiClient";
import {
  CheckoutSessionRequest,
  CheckoutSessionResponse,
} from "../types/Subscription";

// Mentoring Fusion API base URL
const API_URL = process.env.REACT_APP_MENTORING_API_URL ?? "";

/**
 * Service for handling Stripe checkout operations
 * Supports both a la carte and subscription purchases
 */
class CheckoutService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = new ApiClient(API_URL).getClient();
  }

  /**
   * Create a new checkout session for purchasing credits or subscriptions
   * @param checkoutRequest - The checkout session request data
   * @returns Promise<CheckoutSessionResponse> - The checkout session details
   */
  public createCheckoutSession = async (
    checkoutRequest: CheckoutSessionRequest
  ): Promise<CheckoutSessionResponse> => {
    console.log(`Creating checkout session for organization ${checkoutRequest.organization_id}`);
    console.log('Checkout request payload:', JSON.stringify(checkoutRequest, null, 2));

    try {
      const response = await this.apiClient.post<CheckoutSessionResponse>(
        `/checkout/create-checkout-session/${checkoutRequest.organization_id}`,
        checkoutRequest
      );

      console.log(`Checkout session created: ${response.data.id}`);
      return response.data;
    } catch (error: any) {
      console.error('Checkout session creation failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  };

  /**
   * Verify a completed checkout session
   * @param sessionId - The Stripe checkout session ID
   * @returns Promise<any> - The verification result
   */
  public verifyCheckoutSession = async (
    sessionId: string
  ): Promise<any> => {
    console.log(`Verifying checkout session: ${sessionId}`);

    const response = await this.apiClient.post(
      `/checkout/verify-session/${sessionId}`
    );

    console.log(`Session verification result:`, response.data);
    return response.data;
  };

  /**
   * Get checkout session details
   * Note: This endpoint is currently not implemented on the backend
   * @param sessionId - The Stripe checkout session ID
   * @returns Promise<any> - The session details
   */
  public getCheckoutSession = async (
    sessionId: string
  ): Promise<any> => {
    console.log(`Getting checkout session: ${sessionId}`);

    try {
      const response = await this.apiClient.get(
        `/checkout/sessions/${sessionId}`
      );

      return response.data;
    } catch (error: any) {
      // Handle the 501 Not Implemented response from the backend
      if (error.response?.status === 501) {
        console.warn("Get checkout session endpoint is not yet implemented");
        throw new Error("Get checkout session functionality is not yet available");
      }
      throw error;
    }
  };

  /**
   * Create a checkout session for a la carte credit purchase
   * @param organizationId - The organization making the purchase
   * @param planId - The plan ID for the purchase
   * @param priceId - The Stripe price ID for the credits
   * @param successUrl - URL to redirect to on successful payment
   * @param cancelUrl - URL to redirect to on cancelled payment
   * @param metadata - Optional metadata for the session
   * @returns Promise<CheckoutSessionResponse> - The checkout session details
   */
  public createALaCarteCheckout = async (
    organizationId: string,
    planId: string,
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    metadata?: Record<string, string>
  ): Promise<CheckoutSessionResponse> => {
    const checkoutRequest: CheckoutSessionRequest = {
      organization_id: organizationId,
      plan_id: planId,
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: "payment",
      metadata: {
        ...metadata,
        purchase_type: "a_la_carte"
      }
    };

    return this.createCheckoutSession(checkoutRequest);
  };

  /**
   * Create a checkout session for subscription purchase
   * @param organizationId - The organization making the purchase
   * @param planId - The plan ID for the subscription
   * @param priceId - The Stripe price ID for the subscription
   * @param successUrl - URL to redirect to on successful payment
   * @param cancelUrl - URL to redirect to on cancelled payment
   * @param metadata - Optional metadata for the session
   * @returns Promise<CheckoutSessionResponse> - The checkout session details
   */
  public createSubscriptionCheckout = async (
    organizationId: string,
    planId: string,
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    metadata?: Record<string, string>
  ): Promise<CheckoutSessionResponse> => {
    const checkoutRequest: CheckoutSessionRequest = {
      organization_id: organizationId,
      plan_id: planId,
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: "subscription",
      metadata: {
        ...metadata,
        purchase_type: "subscription"
      }
    };

    return this.createCheckoutSession(checkoutRequest);
  };

  /**
   * Handle successful checkout completion
   * This method should be called after a successful payment to verify and process the transaction
   * @param sessionId - The completed checkout session ID
   * @returns Promise<any> - The verification and processing result
   */
  public handleSuccessfulCheckout = async (
    sessionId: string
  ): Promise<any> => {
    console.log(`Handling successful checkout for session: ${sessionId}`);

    try {
      // Verify the session first
      const verificationResult = await this.verifyCheckoutSession(sessionId);

      console.log("Checkout successfully verified and processed");
      return verificationResult;
    } catch (error) {
      console.error("Error handling successful checkout:", error);
      throw error;
    }
  };

  /**
   * Generate success and cancel URLs for checkout sessions
   * @param baseUrl - The base URL of the application
   * @param organizationId - The organization ID
   * @param campaignId - Optional campaign ID for context
   * @returns Object with success and cancel URLs
   */
  public generateCheckoutUrls = (
    baseUrl: string,
    organizationId: string,
    campaignId?: string
  ): { successUrl: string; cancelUrl: string } => {
    const basePath = `${baseUrl}/organizations/${organizationId}`;
    const campaignPath = campaignId ? `/campaigns/${campaignId}` : '';

    return {
      successUrl: `${basePath}${campaignPath}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${basePath}${campaignPath}/checkout/cancelled`
    };
  };
}

export default CheckoutService;
